import { Fragment, useId, useState } from "react";
import type { CSSProperties, MouseEvent, ReactNode } from "react";
import { DefaultLink } from "../../common/DefaultLink";
import { SocialLinks } from "./SocialLinks";
import { usePageFooterSections } from "./sections";
import type { LinkComponentType } from "../../../types/link";
import { FOOTER_COLORS } from "./footerColors";
import { buildSiteEntryLinks, CERTIFICATION_EXAMS, CERTIFICATION_HREF, certificationExamHref } from "../PageHeader/headerNav";

/*
  ヘッダーの帯 (headerNav.ts の buildHeaderNav().bar) のうち、footer にすでに同じ住所の
  リンクがある項目は 2 回並べない (コース一覧は「学習」、料金プランも「学習」に既にある)。
  住所の正本は headerNav.ts 側だけに置き、ここでは写さず読む
*/
const FOOTER_DUPLICATE_BAR_IDS = new Set(["courses", "pricing"]);

const SCENE_NARROW = "/assets/footer-pricing/footer-scene-narrow.svg";
const SCENE_WIDE = "/assets/footer-pricing/footer-scene-wide.svg";

export interface PageFooterProps {
  /**
   * コースリンクのhref属性（デフォルト: "/courses"）
   */
  coursesHref?: string;
  /**
   * 記事一覧リンクのhref属性（デフォルト: "/guides"）
   */
  articlesHref?: string;
  /**
   * AI・IT用語辞典リンクのhref属性（デフォルト: "/glossary"）
   */
  glossaryHref?: string;
  /**
   * 料金プランリンクの行き先。既定は /code/pricing。
   * /pricing は 301 でここへ送っているだけなので、既定にすると
   * 全ページのフッタが毎回1ホップ余計に踏む
   */
  pricingHref?: string;
  /**
   * 求人リンクのhref属性（デフォルト: "/jobs"）
   */
  jobsHref?: string;
  /**
   * ランキングリンクのhref属性（デフォルト: "/ranking"）
   */
  rankingHref?: string;
  /**
   * Linkコンポーネント（react-routerのLinkなど）
   */
  LinkComponent?: LinkComponentType;
  /**
   * お知らせリンクのhref属性（デフォルト: "/news"）
   */
  newsHref?: string;
  /**
   * ヘルプリンクの href。渡したときだけ出す (yumesaku.tech の /help は 2026-10-21 に消した)
   */
  helpHref?: string;
  /**
   * お問い合わせリンクのhref属性（デフォルト: "/contact"）
   */
  contactHref?: string;
  /**
   * Aboutリンクのhref属性（デフォルト: "/about"）
   */
  aboutHref?: string;
  /**
   * プライバシーポリシーリンクのhref属性（デフォルト: "/privacy-policy"）
   */
  privacyHref?: string;
  /**
   * 利用規約リンクのhref属性（デフォルト: "/terms-of-service"）
   */
  termsHref?: string;
  /**
   * 特定商取引法に基づく表示リンクのhref属性
   */
  tokushohoHref?: string;
  /**
   * 使われていない。フッターは全頁で黒めの面に固定した (2026-10-20)。
   * 呼び出し側の指定を壊さないために型だけ残している
   * @deprecated 渡しても見た目は変わらない
   */
  tone?: "light" | "dark";
}

/* 短い区画は、親の区画と同じ列の下に縦に重ねる (上段の 求人 と サポート と同じ組み方)。子は単独の列を持たない */
const STACKED_UNDER: Record<string, string | undefined> = {
  "ai-coding": "languages",
};

/* 下段の区画の幅。5 列の格子の上で、項目の多い欄に 2 列分を渡す */
const SECTION_SPAN: Record<string, string | undefined> = {
  visualize: "lg:col-span-2",
  git: "lg:col-span-2",
  "computer-science": "lg:col-span-2",
  security: "lg:col-span-2",
};


/*
  見出しごとの畳み。PC (md 以上) は今の列のまま常に開き、見出しは押せない。
  スマホは見出しの行 (高さ 48px) を押して開け閉めする。
  初めの HTML (SSR) の時点でスマホは閉じた姿にし (CSS だけで隠す)、JS は押されたときに開けるだけ。
  読み込み直後に開いた姿が一瞬見えることは無い。リンクは DOM に残るので検索にも読まれる
*/
function FooterFold({ title, className, tight, children }: { title: string; className?: string; tight?: boolean; children: ReactNode }) {
  const c = FOOTER_COLORS.dark;
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  return (
    <div className={`${className ?? ""} max-md:border-b`} style={{ borderColor: c.border }} data-footer-fold>
      <h4 className={`m-0 text-sm font-semibold max-md:text-base ${tight ? "md:mb-3" : "md:mb-4"}`}>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => {
            /* PC は常に開いているので、キーボードで押されても状態を変えない */
            if (window.matchMedia("(min-width: 768px)").matches) return;
            setOpen((v) => !v);
          }}
          className="flex w-full items-center justify-between bg-transparent p-0 text-left font-[inherit] text-[length:inherit] max-md:min-h-12 md:pointer-events-none md:cursor-default"
          style={{ color: c.head, border: 0 }}
        >
          {title}
          <svg
            className={`shrink-0 transition-transform duration-200 md:hidden ${open ? "rotate-180" : ""}`}
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden
          >
            <path d="M5 8l5 5 5-5" stroke={c.link} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </h4>
      <div id={bodyId} className={`max-md:pb-3 ${open ? "" : "max-md:hidden"}`}>{children}</div>
    </div>
  );
}

export function PageFooter({
  coursesHref = "/learn/courses",
  articlesHref = "/guides",
  glossaryHref = "/glossary",
  pricingHref = "/code/pricing",
  jobsHref = "/jobs",
  rankingHref = "/ranking",
  newsHref = "/news",
  helpHref,
  contactHref = "/contact",
  aboutHref = "/about",
  privacyHref = "/privacy-policy",
  termsHref = "/terms-of-service",
  tokushohoHref,
  LinkComponent = DefaultLink,
}: PageFooterProps) {
  const c = FOOTER_COLORS.dark;
  const sections = usePageFooterSections();
  /*
    ヘッダーの帯に出ている主な入口。並びと住所は headerNav.ts の buildHeaderNav が正本。
    「転職」は帯では窓 (ドロップダウン) の入口だが、footer は今の作りがどの節も畳んだ
    フラットなリンクの列挙 (窓を持たない) なので、中身 4 つを並べず入口の住所 1 つだけを置く
  */
  const mainEntryLinks = buildSiteEntryLinks({ pricingHref, exclude: FOOTER_DUPLICATE_BAR_IDS });
  /* スマホで常に出す、よく使う 6 つ。畳んだ中の同じリンクを写している */
  const quickLinks: { to: string; label: string }[] = [
    { to: coursesHref, label: "コース一覧" },
    { to: pricingHref, label: "料金プラン" },
    { to: articlesHref, label: "学習ガイド一覧" },
    { to: glossaryHref, label: "AI・IT用語辞典" },
    { to: jobsHref, label: "求人一覧" },
    { to: contactHref, label: "お問い合わせ" },
  ];
  const linkStyle: CSSProperties = {
    fontSize: "0.875rem",
    color: c.link,
    textDecoration: "none",
  };

  /*
    スマホでは押す所の高さを 44px にする。字の大きさと並びは変えず、リンクの高さと行の間だけを入れ替える
    (行の間 0.5rem を 0 にして、リンクを 44px にする)。隣のリンクと当たりは重ならない
  */
  const linkClass = "max-md:inline-flex max-md:min-h-11 max-md:items-center";

  const handleMouseEnter = (e: MouseEvent<HTMLAnchorElement>) => {
    e.currentTarget.style.color = c.hover;
  };

  const handleMouseLeave = (e: MouseEvent<HTMLAnchorElement>) => {
    e.currentTarget.style.color = c.link;
  };

  return (
    <footer className="ui-page-footer"
      style={{
        background: c.bg,
      }}
    >
      {/* 頭の場面。いちばん手前の丘はフッターの地と同じ色で、下の面へつながる */}
      <picture>
        <source media="(min-width: 768px)" srcSet={SCENE_WIDE} />
        <img
          src={SCENE_NARROW}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="block aspect-[780/440] w-full object-cover md:aspect-[1440/280] md:max-h-[320px]"
        />
      </picture>
      <div
        className="mx-auto max-w-[800px] px-8 py-12 lg:max-w-[1120px] lg:px-6 xl:px-8"
      >
        <nav aria-label="よく使うリンク" className="mb-6 grid grid-cols-2 gap-3 md:hidden">
          {quickLinks.map((q) => (
            <LinkComponent
              key={q.to}
              to={q.to}
              className="inline-flex min-h-12 items-center justify-center rounded-lg px-3 text-center font-semibold"
              style={{
                color: c.head,
                background: c.tile,
                textDecoration: "none",
                boxShadow: "4px 4px 0 rgba(0, 0, 0, 0.45)",
              }}
            >
              {q.label}
            </LinkComponent>
          ))}
        </nav>

        {/* 1024px 以上は 5 列。タブレットとスマホは今の auto-fit のまま */}
        <div
          className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-8 max-md:gap-0 lg:grid-cols-5 lg:gap-x-4 xl:gap-x-8"
          style={{
            marginBottom: "2rem",
          }}
        >
          {/* 学習 */}
          <FooterFold title="学習">
<ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to={coursesHref}
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  コース一覧
                </LinkComponent>
              </li>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to="/learn/lessons"
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  レッスン一覧
                </LinkComponent>
              </li>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to={articlesHref}
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  学習ガイド一覧
                </LinkComponent>
              </li>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to={glossaryHref}
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  AI・IT用語辞典
                </LinkComponent>
              </li>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to={pricingHref}
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  料金プラン
                </LinkComponent>
              </li>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to={rankingHref}
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  ランキング
                </LinkComponent>
              </li>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to="/portfolio"
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  ポートフォリオ
                </LinkComponent>
              </li>
            </ul>
</FooterFold>

          {/* 主な機能。ヘッダーの帯と同じ並び (headerNav.ts が正本) */}
          <FooterFold title="主な機能">
<ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {mainEntryLinks.map((item) =>
                item.external ? (
                  <li key={item.id} className="mb-2 max-md:mb-0">
                    <a
                      href={item.to}
                      style={linkStyle}
                      className={linkClass}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      {item.label}
                    </a>
                  </li>
                ) : (
                  <li key={item.id} className="mb-2 max-md:mb-0">
                    <LinkComponent
                      to={item.to}
                      style={linkStyle}
                      className={linkClass}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      {item.label}
                    </LinkComponent>
                  </li>
                )
              )}
            </ul>
</FooterFold>

          {/*
            IT資格取得 (2026-10-25)。入口と、試験の頁を月の検索数の多い順に並べる。並びと名前の正本は headerNav.ts の CERTIFICATION_EXAMS
          */}
          <FooterFold title="IT資格取得">
<ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to={CERTIFICATION_HREF}
                  style={linkStyle}
                  className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  目的別の選び方
                </LinkComponent>
              </li>
              {CERTIFICATION_EXAMS.map((exam) => (
                <li key={exam.slug} className="mb-2 break-keep max-md:mb-0">
                  <LinkComponent
                    to={certificationExamHref(exam.slug)}
                    style={linkStyle}
                    className={linkClass}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    {/* 狭い列で語の途中で折らない。折るのは breakParts の区切りと空白だけ */}
                    {exam.breakParts
                      ? exam.breakParts.map((part, i) => (
                          <Fragment key={part}>
                            {i > 0 ? <wbr /> : null}
                            {part}
                          </Fragment>
                        ))
                      : exam.label}
                  </LinkComponent>
                </li>
              ))}
            </ul>
</FooterFold>

          {/* ツール */}
          <FooterFold title="ツール">
<ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to="/tools"
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  ツール一覧
                </LinkComponent>
              </li>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to="/typing"
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  AIタイピング
                </LinkComponent>
              </li>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to="https://yumesaku.tech/tools/typing/ranking"
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  タイピング順位表
                </LinkComponent>
              </li>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to="/tools/playground"
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  コードの実行環境
                </LinkComponent>
              </li>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to="/tekishoku-shindan"
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  適職診断
                </LinkComponent>
              </li>
            </ul>
</FooterFold>

          {/* 求人 と サポート。短い欄なので、1024px 以上では同じ列に縦に重ねる */}
          <div className="contents lg:flex lg:flex-col lg:gap-8">
          <FooterFold title="求人">
<ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to={jobsHref}
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  求人一覧
                </LinkComponent>
              </li>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to="/companies"
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  企業一覧
                </LinkComponent>
              </li>
            </ul>
</FooterFold>

          {/* サポート */}
          <FooterFold title="サポート">
<ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to={newsHref}
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  お知らせ
                </LinkComponent>
              </li>
              {helpHref && (
                <li className="mb-2 max-md:mb-0">
                  <LinkComponent
                    to={helpHref}
                    style={linkStyle}
                    className={linkClass}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    ヘルプ
                  </LinkComponent>
                </li>
              )}
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to={contactHref}
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  お問い合わせ
                </LinkComponent>
              </li>
            </ul>
</FooterFold>

          </div>

          {/* 関連サービス */}
          <FooterFold title="関連サービス">
<ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to="/lab"
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  ゆめさく研究所
                </LinkComponent>
              </li>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to="/skills"
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  ゆめさくスキル
                </LinkComponent>
              </li>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to="/women"
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  ゆめさくエンジニア Women
                </LinkComponent>
              </li>
            </ul>
</FooterFold>

          {/* 運営情報 */}
          <FooterFold title="運営情報">
<ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to={aboutHref}
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  ゆめさくコードについて
                </LinkComponent>
              </li>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to={privacyHref}
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  プライバシーポリシー
                </LinkComponent>
              </li>
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to={termsHref}
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  利用規約
                </LinkComponent>
              </li>
              {tokushohoHref && (
                <li className="mb-2 max-md:mb-0">
                  <LinkComponent
                    to={tokushohoHref}
                    style={linkStyle}
 className={linkClass}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    特定商取引法に基づく表示
                  </LinkComponent>
                </li>
              )}
              <li className="mb-2 max-md:mb-0">
                <LinkComponent
                  to="/sitemap"
                  style={linkStyle}
 className={linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  サイトマップ
                </LinkComponent>
              </li>
            </ul>
</FooterFold>
        </div>


        {sections.length > 0 && (
          <div
            className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-8 border-t pt-8 max-md:gap-0 max-md:border-t-0 max-md:pt-0 lg:grid-cols-5 lg:gap-x-4 xl:gap-x-8"
            style={{
              marginBottom: "2rem",
              borderColor: c.border,
            }}
          >
            {sections.filter((section) => !STACKED_UNDER[section.id]).map((section) => {
              const stacked = sections.filter((child) => STACKED_UNDER[child.id] === section.id);
              const renderSection = (section: (typeof sections)[number]) => {
              /* 小見出しの無い組が先頭なら、「すべて見る」をその組の先頭に並べる (空きも字下げも付けない) */
              const allInFirstGroup = section.groups[0]?.label === "";
              /* 別アプリの住所 (external) は、アプリ内の遷移にせず素の a で開く */
              const SectionLink = section.external ? DefaultLink : LinkComponent;
              const allLink = (
                <SectionLink
                  to={section.allHref}
                  style={linkStyle}
                  className={allInFirstGroup ? `${linkClass} max-md:px-2` : linkClass}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  すべて見る
                </SectionLink>
              );
              return (
                <FooterFold key={section.id} className={SECTION_SPAN[section.id]} title={section.heading} tight>
                  {!allInFirstGroup && <p style={{ margin: "0 0 0.75rem" }}>{allLink}</p>}
                  {section.groups.map((group, groupIndex) => (
                    <div key={group.label || "all"} style={{ marginBottom: "0.75rem" }}>
                      {group.label && (
                        <p
                          style={{
                            fontSize: "0.875rem",
                            color: c.copy,
                            margin: "0 0 0.25rem",
                          }}
                        >
                          {group.label}
                        </p>
                      )}
                      <ul
                        className="m-0 flex flex-wrap gap-x-4 gap-y-1 max-md:-mx-2 max-md:gap-x-0 max-md:gap-y-0"
                        style={{ listStyle: "none", padding: 0 }}
                      >
                        {allInFirstGroup && groupIndex === 0 && (
                          <li>{allLink}</li>
                        )}
                        {group.links.map((link) => (
                          <li key={link.href}>
                            <SectionLink
                              to={link.href}
                              style={linkStyle}
                              className={`${linkClass} max-md:px-2`}
                              onMouseEnter={handleMouseEnter}
                              onMouseLeave={handleMouseLeave}
                            >
                              {link.title}
                            </SectionLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </FooterFold>
              );
              };
              if (stacked.length === 0) {
                return renderSection(section);
              }
              return (
                <div key={section.id} className="flex flex-col gap-8 max-md:gap-0">
                  {renderSection(section)}
                  {stacked.map((child) => renderSection(child))}
                </div>
              );
            })}
          </div>
        )}

        <SocialLinks
          align="center"
          tone="dark"
        />

        <div
          style={{
            borderTop: `1px solid ${c.border}`,
            marginTop: "1.5rem",
            paddingTop: "2rem",
            textAlign: "center",
          }}
        >
          <p
            style={{
              fontSize: "0.875rem",
              color: c.copy,
              margin: 0,
            }}
          >
            © {new Date().getFullYear()} ゆめさくコード. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
